import React, { useCallback, useEffect, useState } from "react";
import { Box,useTheme, useMediaQuery } from "@mui/material";
import CustomTabs from "../../common/custom-tabs";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";
import Filter from "../Filter";
import ReportModal from "./ReportModal";
import AdvisorCallModal from "./AdvisorCallModal";
import { useAuth } from "../../hooks/AuthProvider";
import {
  MFD_REPORT_REQUESTS_ENDPOINT,
  MFD_ADVISOR_CALLS_ENDPOINT,
} from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

// ─── Constants ────────────────────────────────────────────────────────────────

const ALL_TABS = [
  { value: "reports",       label: "Reports",       permission: "mfd_report_requests.view" },
  { value: "advisor_calls", label: "Advisor Calls", permission: "mfd_advisor_calls.view" },
];

const REPORT_FILTER_FIELDS = [
  { label: "Status", type: "single_select_list", values: ["Pending", "Completed"] },
];

const ADVISOR_FILTER_FIELDS = [
  { label: "Status", type: "single_select_list", values: ["Pending", "Completed"] },
];

// ─── Component ────────────────────────────────────────────────────────────────

const Requests = () => {
  const { getValidToken, permissions } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const visibleTabs = ALL_TABS.filter(
    (tab) => !tab.permission || permissions?.includes(tab.permission)
  );

  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.value ?? "reports");
  const isAdvisor = activeTab === "advisor_calls";

  // Reports state
  const [reports,        setReports]        = useState([]);
  const [reportsTotal,   setReportsTotal]   = useState(0);
  const [reportsPage,    setReportsPage]    = useState(0);
  const [reportsRpp,     setReportsRpp]     = useState(20);
  const [reportsSearch,  setReportsSearch]  = useState("");
  const [reportsFilters, setReportsFilters] = useState([]);
  const [reportTemplate, setReportTemplate] = useState(null);

  // Advisor calls state
  const [calls,        setCalls]        = useState([]);
  const [callsTotal,   setCallsTotal]   = useState(0);
  const [callsPage,    setCallsPage]    = useState(0);
  const [callsRpp,     setCallsRpp]     = useState(20);
  const [callsSearch,  setCallsSearch]  = useState("");
  const [callsFilters, setCallsFilters] = useState([]);

  const [loading,     setLoading]     = useState(false);
  const [modalOpen,   setModalOpen]   = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // ── Auth ──────────────────────────────────────────────────────────────────

  const getHeaders = useCallback(async () => {
    const token  = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [getValidToken]);

  // ── Fetchers ──────────────────────────────────────────────────────────────

  const applyFilters = (params, filters) => {
    filters.forEach(({ field, operator, value }) => {
      params.append(
        field.toLowerCase().replaceAll(" ", "_"),
        `${operator.toLowerCase().replaceAll(" ", "_")}:${value ?? ""}`
      );
    });
  };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: reportsRpp, offset: reportsPage * reportsRpp });
    if (reportsSearch.trim()) params.set("search", reportsSearch.trim());
    applyFilters(params, reportsFilters);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${MFD_REPORT_REQUESTS_ENDPOINT}?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch report requests");
      const data = await res.json();
      setReports(data.results ?? []);
      setReportsTotal(data.count ?? 0);
      setReportTemplate(data.template ?? null);
    } catch (err) {
      console.error("Error fetching report requests:", err);
    } finally {
      setLoading(false);
    }
  }, [reportsPage, reportsRpp, reportsSearch, reportsFilters, getHeaders]);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: callsRpp, offset: callsPage * callsRpp });
    if (callsSearch.trim()) params.set("search", callsSearch.trim());
    applyFilters(params, callsFilters);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${MFD_ADVISOR_CALLS_ENDPOINT}?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch advisor calls");
      const data = await res.json();
      setCalls(data.results ?? []);
      setCallsTotal(data.count ?? 0);
    } catch (err) {
      console.error("Error fetching advisor calls:", err);
    } finally {
      setLoading(false);
    }
  }, [callsPage, callsRpp, callsSearch, callsFilters, getHeaders]);

  useEffect(() => { fetchReports(); }, [fetchReports]);
  useEffect(() => { fetchCalls();   }, [fetchCalls]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleOpenModal = (row) => { setSelectedRow(row); setModalOpen(true); };
  const handleCloseModal = ()   => { setModalOpen(false); setSelectedRow(null); };

  const handleReportSend = async (updatedRow) => {
    try {
      const headers = await getHeaders();
      await fetch(`${MFD_REPORT_REQUESTS_ENDPOINT}${updatedRow.id}/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status: "Completed", note: updatedRow.note }),
      });
      fetchReports();
    } catch (err) {
      console.error("Error updating report request:", err);
    } finally {
      handleCloseModal();
    }
  };

  const handleCallComplete = async (updatedRow) => {
    try {
      const headers = await getHeaders();
      await fetch(`${MFD_ADVISOR_CALLS_ENDPOINT}${updatedRow.id}/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status: "Completed", note: updatedRow.note }),
      });
      fetchCalls();
    } catch (err) {
      console.error("Error updating advisor call:", err);
    } finally {
      handleCloseModal();
    }
  };

  // ── Tab-aware derived state ────────────────────────────────────────────────

  const rows         = isAdvisor ? calls        : reports;
  const totalCount   = isAdvisor ? callsTotal   : reportsTotal;
  const page         = isAdvisor ? callsPage    : reportsPage;
  const rpp          = isAdvisor ? callsRpp     : reportsRpp;
  const search       = isAdvisor ? callsSearch  : reportsSearch;
  const filters      = isAdvisor ? callsFilters : reportsFilters;
  const filterFields = isAdvisor ? ADVISOR_FILTER_FIELDS : REPORT_FILTER_FIELDS;

  const handlePageChange = (_, newPage) => {
    isAdvisor ? setCallsPage(newPage) : setReportsPage(newPage);
  };
  const handleRppChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (isAdvisor) { setCallsRpp(val); setCallsPage(0); }
    else           { setReportsRpp(val); setReportsPage(0); }
  };
  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (isAdvisor) { setCallsSearch(val); setCallsPage(0); }
    else           { setReportsSearch(val); setReportsPage(0); }
  };
  const handleFilterApply = (newFilters) => {
    if (isAdvisor) { setCallsFilters(newFilters); setCallsPage(0); }
    else           { setReportsFilters(newFilters); setReportsPage(0); }
  };

  // ── Column definitions ────────────────────────────────────────────────────

  const reportColumns = [
    { id: "client_name", label: "Client Name", minWidth: 160 },
    { id: "pan",         label: "PAN",         minWidth: 130 },
    { id: "client_code", label: "Client Code", minWidth: 130 },
    { id: "type",        label: "Report Type", minWidth: 200 },
    { id: "period",      label: "Period",      minWidth: 130 },
    {
      id: "created_at",
      label: "Requested At",
      minWidth: 160,
      render: (row) => formatWithTzLabel(row.created_at),
    },
    { id: "status", label: "Status", minWidth: 110 },
  ];

  const advisorColumns = [
    { id: "client_name",     label: "Client Name", minWidth: 150 },
    { id: "client_phone_no", label: "Phone",       minWidth: 140 },
    { id: "pan",             label: "PAN",         minWidth: 130 },
    { id: "client_code",     label: "Client Code", minWidth: 130 },
    { id: "type",            label: "Request",     minWidth: 160 },
    {
      id: "detail",
      label: "Detail",
      minWidth: 220,
      render: (row) => row.detail || "—",
    },
    {
      id: "note",
      label: "Note",
      minWidth: 180,
      render: (row) => row.note || "—",
    },
    {
      id: "created_at",
      label: "Requested At",
      minWidth: 160,
      render: (row) => (row.created_at ? formatWithTzLabel(row.created_at) : "—"),
    },
    { id: "status", label: "Status", minWidth: 110 },
  ];

  const handleRowClick = (row) => {
    if (row.status === "Completed") return;
    handleOpenModal(row);
  };

  const columns = isAdvisor ? advisorColumns : reportColumns;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <CustomTabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          tabs={visibleTabs}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center", flexDirection: { xs: "column", sm: "row" },  alignItems: { xs: "stretch", sm: "center" }, }}>
        <Box sx={{ flex: 1, maxWidth: { xs: "100%", md: 500 }, width: "100%", }}>
          <TableSearchInput
            searchQuery={search}
            onSearchChange={handleSearchChange}
            placeholder={
              isMobile
              ? "Search..."
              : isAdvisor
              ? "Search by Client Name, Phone, PAN, Client Code, Request or Details"
              : "Search by Client Name, PAN or Client Code"
            }
          />
        </Box>
        <Filter
          fields={filterFields}
          onApply={handleFilterApply}
          initalFilters={filters}
        />
      </Box>

      <AppTable
        columns={columns}
        rows={rows}
        rowKey="id"
        loading={loading}
        emptyMessage={isAdvisor ? "No advisor calls found" : "No pending requests"}
        maxHeight={620}
        hideMainScroll={false}
        containerSx={{ overflowX: "auto" }}
        count={totalCount}
        page={page}
        rowsPerPage={rpp}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRppChange}
        onRowClick={handleRowClick}
      />

      {isAdvisor ? (
        <AdvisorCallModal
          open={modalOpen}
          row={selectedRow}
          onClose={handleCloseModal}
          onComplete={handleCallComplete}
        />
      ) : (
        <ReportModal
          open={modalOpen}
          row={selectedRow}
          template={reportTemplate}
          onClose={handleCloseModal}
          onSend={handleReportSend}
        />
      )}
    </Box>
  );
};

export default Requests;
